import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient, ApolloError } from '@apollo/client';
import { message, Modal, notification } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../../../../api';
import { type SimpleListItemProps } from '../../../../components/SimpleListItem';

const useSessionsData = () => {
    const { t } = useTranslation(['userSelf', 'common']);
    const apolloClient = useApolloClient();

    const { data, refetch, loading } = api.useGetAuthenticatedUserSessionsQuery({
        fetchPolicy: 'cache-and-network',
    });

    const revokeSession = useCallback(
        (sessionId: string) => {
            Modal.confirm({
                title: t('userSelf:sessions.revokeModal.title'),
                icon: <ExclamationCircleOutlined />,
                content: t('userSelf:sessions.revokeModal.content'),
                okText: t('userSelf:sessions.revokeModal.okText'),
                okType: 'danger',
                cancelText: t('userSelf:sessions.revokeModal.cancelText'),
                onOk: async () => {
                    try {
                        await apolloClient.mutate<api.RevokeSessionMutation, api.RevokeSessionMutationVariables>({
                            mutation: api.RevokeSessionDocument,
                            variables: { sessionId, displayNotice: true },
                        });

                        // also refetch keys
                        refetch();

                        // notify the user
                        // display a notification
                        notification.success({
                            message: t('userSelf:sessions.deleteMessage.title'),
                            description: t('userSelf:sessions.deleteMessage.description'),
                        });
                    } catch (error) {
                        if (error instanceof ApolloError) {
                            message.error(error.graphQLErrors[0].message);
                        }
                    }
                },
            });
        },
        [apolloClient, refetch, t]
    );

    const sessions = useMemo(
        () =>
            (data?.currentUser?.sessions || [])
                .map(key => ({
                    ...key,
                    lastActivityAt: key.lastActivityAt ? new Date(key.lastActivityAt) : null,
                }))
                .sort((a, b) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime()),
        [data]
    );

    const sessionsData = useMemo(
        () =>
            sessions.map(session => {
                const items: SimpleListItemProps[] = [
                    {
                        key: 'userAgent',
                        label: t('userSelf:sessions.table.titles.userAgent'),
                        title: session.userAgent,
                        type: 'large',
                    },
                    {
                        key: 'ip',
                        label: t('userSelf:sessions.table.titles.ip'),
                        title: session.ip,
                    },
                    {
                        key: 'country',
                        label: t('userSelf:sessions.table.titles.country'),
                        title: session.country ?? '-',
                    },
                    {
                        key: 'lastActivityAt',
                        label: t('userSelf:sessions.table.titles.lastActivityAt'),
                        title: t('common:formats.dateTime', { date: session.lastActivityAt }),
                    },
                ];

                return {
                    id: session.id,
                    items,
                };
            }),
        [sessions, t]
    );

    return {
        sessionsData,
        revokeSession,
        loading,
    };
};

export default useSessionsData;
